/**
 * The "View as user" log for super admins (TRUS-747): every session with its
 * ticket, classification, reason and outcome, and a drill-down into what
 * happened in it (writes allowed / refused, pages viewed, records changed).
 */
import { Fragment, useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, RefreshCw, StopCircle } from 'lucide-react';
import { authFetch } from '@/lib/authFetch';
import { useFormattedDate } from '../../hooks/useUserTimezone';

interface SessionRow {
  id: number;
  actor_name: string | null;
  actor_email: string | null;
  target_user_id: number;
  target_name: string | null;
  target_email: string | null;
  target_kind: 'staff' | 'member';
  target_role: string | null;
  cbo_id: number;
  cbo_name: string | null;
  mode: 'read_only' | 'read_write';
  ticket_key: string;
  ticket_url?: string | null;
  jira_ticket_created?: boolean;
  jira_summary_posted_at?: string | null;
  classification: string;
  classification_label: string;
  reason: string;
  started_at: string;
  expires_at: string;
  ended_at: string | null;
  end_reason: string | null;
  notified_at: string | null;
  notify_email_sent: boolean | null;
  writes_allowed: number;
  writes_blocked: number;
  requests: number;
}

interface SessionDetail {
  requests: { occurred_at: string; method: string; path: string; status_code: number | null; outcome: string; rule: string | null }[];
  pages: { occurred_at: string; event_type: string; path: string; page_title: string | null; action_name: string | null }[];
  changes: { occurred_at: string; entity_type: string; entity_id: number; entity_name: string | null; action: string }[];
}

interface Props {
  /** Limit to one org (e.g. the org currently open in User Management). */
  cboId?: number;
  /** Limit to sessions as one user. */
  targetUserId?: number;
}

export function ImpersonationLog({ cboId, targetUserId }: Props) {
  const { fmt } = useFormattedDate();
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState<SessionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [detail, setDetail] = useState<Record<number, SessionDetail>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (cboId) params.set('cbo_id', String(cboId));
      if (targetUserId) params.set('target_user_id', String(targetUserId));
      const res = await authFetch(`/api/impersonation/sessions?${params}`, { skipCboHeader: true });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(data.sessions || []);
    } catch (e: any) {
      setError(`Could not load the log (${e?.message || 'error'}).`);
    } finally {
      setLoading(false);
    }
  }, [days, cboId, targetUserId]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = async (id: number) => {
    if (openId === id) {
      setOpenId(null);
      return;
    }
    setOpenId(id);
    if (!detail[id]) {
      const res = await authFetch(`/api/impersonation/sessions/${id}/events`, { skipCboHeader: true });
      if (res.ok) {
        const data = await res.json();
        setDetail((d) => ({ ...d, [id]: data }));
      }
    }
  };

  const endSession = async (id: number) => {
    await authFetch(`/api/impersonation/sessions/${id}/end`, { method: 'POST', skipCboHeader: true });
    void load();
  };

  const status = (r: SessionRow) => {
    if (r.ended_at) return r.end_reason || 'ended';
    if (Date.parse(r.expires_at) <= Date.now()) return 'expired';
    return 'open';
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Every time a super admin viewed the app as someone else: why, for how long, and what they did.
        </p>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            aria-label="Time window"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">{error}</div>}

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <table className="w-full text-sm">
          <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900">
            <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              <th className="w-6 px-3 py-3" />
              <th className="px-3 py-3">Started</th>
              <th className="px-3 py-3">Super admin</th>
              <th className="px-3 py-3">Viewed as</th>
              <th className="px-3 py-3">Ticket</th>
              <th className="px-3 py-3">Access</th>
              <th className="px-3 py-3 text-right">Requests</th>
              <th className="px-3 py-3 text-right">Writes</th>
              <th className="px-3 py-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-3 py-8 text-center text-gray-500">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-3 py-8 text-center text-gray-500 dark:text-gray-400">
                  No View as user sessions in this period.
                </td>
              </tr>
            ) : (
              rows.map((r) => {
                const st = status(r);
                const d = detail[r.id];
                return (
                  <Fragment key={r.id}>
                    <tr className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50" onClick={() => void toggle(r.id)}>
                      <td className="px-3 py-3 text-gray-400">
                        {openId === r.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-gray-900 dark:text-white">{fmt.dateTime(r.started_at)}</td>
                      <td className="px-3 py-3 text-gray-900 dark:text-white">{r.actor_name || r.actor_email}</td>
                      <td className="px-3 py-3">
                        <div className="text-gray-900 dark:text-white">{r.target_name || r.target_email}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {r.target_kind === 'member' ? 'Member portal' : r.target_role} · {r.cbo_name}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="font-mono text-xs text-gray-900 dark:text-white">
                          {r.ticket_url ? (
                            <a
                              href={r.ticket_url}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-blue-600 hover:underline dark:text-blue-400"
                            >
                              {r.ticket_key}
                            </a>
                          ) : (
                            r.ticket_key
                          )}
                          {r.jira_ticket_created && <span className="ml-1 text-[10px] text-gray-400">auto</span>}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{r.classification_label}</div>
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                            r.mode === 'read_write'
                              ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                              : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                          }`}
                        >
                          {r.mode === 'read_write' ? 'Read and write' : 'Read only'}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-gray-900 dark:text-white">{r.requests}</td>
                      <td className="px-3 py-3 text-right tabular-nums">
                        <span className="text-gray-900 dark:text-white">{r.writes_allowed}</span>
                        {r.writes_blocked > 0 && (
                          <span className="ml-1 text-xs text-red-600 dark:text-red-400">({r.writes_blocked} refused)</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        {st === 'open' ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              void endSession(r.id);
                            }}
                            className="inline-flex items-center gap-1 rounded bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700"
                          >
                            <StopCircle className="h-3.5 w-3.5" /> End
                          </button>
                        ) : (
                          <span className="text-xs text-gray-500 dark:text-gray-400">{st.replace(/_/g, ' ')}</span>
                        )}
                      </td>
                    </tr>
                    {openId === r.id && (
                      <tr className="bg-gray-50 dark:bg-gray-900/40">
                        <td />
                        <td colSpan={8} className="space-y-3 px-3 py-3 text-xs text-gray-700 dark:text-gray-300">
                          <div>
                            <span className="font-semibold">Reason: </span>
                            <span className="whitespace-pre-wrap">{r.reason}</span>
                          </div>
                          <div>
                            <span className="font-semibold">User notified: </span>
                            {r.notified_at
                              ? `${fmt.dateTime(r.notified_at)}${r.notify_email_sent ? ' (email sent)' : ' (email not sent)'}`
                              : 'pending'}
                          </div>
                          {!d ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <div className="grid gap-3 lg:grid-cols-3">
                              <DetailList
                                title="Writes and refusals"
                                empty="None"
                                items={d.requests.map((e) => ({
                                  at: e.occurred_at,
                                  text: `${e.method} ${e.path}`,
                                  tag: e.outcome === 'allowed' ? `${e.status_code ?? ''}` : `${e.outcome}: ${e.rule ?? ''}`,
                                  bad: e.outcome !== 'allowed',
                                }))}
                                fmt={fmt.time}
                              />
                              <DetailList
                                title="Pages viewed"
                                empty="None recorded"
                                items={d.pages.map((p) => ({
                                  at: p.occurred_at,
                                  text: p.page_title || p.action_name || p.path,
                                  tag: p.event_type === 'page_view' ? '' : p.event_type,
                                }))}
                                fmt={fmt.time}
                              />
                              <DetailList
                                title="Records changed"
                                empty="None"
                                items={d.changes.map((c) => ({
                                  at: c.occurred_at,
                                  text: `${c.entity_type} #${c.entity_id}${c.entity_name ? ` (${c.entity_name})` : ''}`,
                                  tag: c.action,
                                }))}
                                fmt={fmt.time}
                              />
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DetailList({
  title,
  items,
  empty,
  fmt,
}: {
  title: string;
  items: { at: string; text: string; tag?: string; bad?: boolean }[];
  empty: string;
  fmt: (iso: string) => string;
}) {
  return (
    <div>
      <div className="mb-1 font-semibold">
        {title} ({items.length})
      </div>
      {items.length === 0 ? (
        <div className="text-gray-500">{empty}</div>
      ) : (
        <ul className="max-h-56 space-y-1 overflow-y-auto">
          {items.map((it, i) => (
            <li key={i} className="flex gap-2">
              <span className="shrink-0 tabular-nums text-gray-500">{fmt(it.at)}</span>
              <span className="min-w-0 break-all">{it.text}</span>
              {it.tag && (
                <span className={`shrink-0 ${it.bad ? 'text-red-600 dark:text-red-400' : 'text-gray-500'}`}>{it.tag}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default ImpersonationLog;
